import { CustomFieldsEmptyState } from './CustomFieldsEmptyState';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { CustomFieldsLevel } from 'hooks/useCustomFieldRules';
import { ListView, type Column, type Row } from '@proposify/list-view';
import { ErrorBoundary } from 'react-error-boundary';
import { useSuspenseQuery, useQueryErrorResetBoundary, useIsMutating } from '@tanstack/react-query';
import { Suspense, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { trackError } from '@proposify/frontend-observability';
import { formatFieldType } from './formatFieldType';
import { DeleteCustomFieldConfirmDialog } from './DeleteCustomFieldConfirmDialog';
import type { AssignedCustomFieldsQueryOptions } from 'services/custom-fields/customFieldsQueries';
import type { AssignedCustomField } from 'services/custom-fields/schemas';
import { MutationKeys } from 'utils/mutationKeys';

type AssignedCustomFieldsProps = {
  level: CustomFieldsLevel;
  query: AssignedCustomFieldsQueryOptions;
  onDelete?: (customFieldId: string) => void;
};

type ColumnKeys = ['name', 'type'];

export const buildColumns = (t: TFunction): Column<ColumnKeys>[] => [
  {
    field: 'name',
    headerName: t('editor.variables.custom_field'),
    flex: 1,
    width: 200,
    align: 'left',
  },
  {
    field: 'type',
    headerName: t('editor.variables.field_type'),
    flex: 1,
    width: 200,
    align: 'left',
  },
];

export function AssignedCustomFieldsLoader({ level, query, onDelete }: AssignedCustomFieldsProps) {
  const { t } = useTranslation();
  const columns = useMemo(() => buildColumns(t), [t]);
  const { data: assignedCustomFields } = useSuspenseQuery(query);
  const isAssigning = useIsMutating({ mutationKey: MutationKeys.assignCustomField }) > 0;
  const isUnassigning = useIsMutating({ mutationKey: MutationKeys.unassignCustomField }) > 0;

  const [confirmingField, setConfirmingField] = useState<AssignedCustomField | null>(null);

  const rows: Row<ColumnKeys>[] = useMemo(
    () =>
      assignedCustomFields.map((field) => ({
        id: String(field.id),
        name: (
          <Typography variant="body2" fontWeight={600} color="text.primary">
            {field.friendlyName}
          </Typography>
        ),
        type: (
          <Typography variant="body2" color="text.secondary">
            {formatFieldType(field.type, t)}
          </Typography>
        ),
        visibleActions: null,
        hoverActions: onDelete ? (
          <IconButton
            size="small"
            aria-label={t('editor.variables.delete_custom_field_aria', 'Delete {{fieldName}}', { fieldName: field.friendlyName })}
            onClick={(e) => {
              e.stopPropagation();
              setConfirmingField(field);
            }}
          >
            <DeleteOutlineRoundedIcon fontSize="small" />
          </IconButton>
        ) : null,
        onClick: () => {
          // do nothing
        },
      })),
    [assignedCustomFields, t, onDelete]
  );

  return (
    <>
      <ListView
        withHeader
        loading={isAssigning || isUnassigning}
        EmptyState={
          <CustomFieldsEmptyState
            sx={{
              paddingTop: 8,
            }}
            title={t('editor.variables.no_custom_fields_yet', 'No custom fields yet')}
            subtitle={t('editor.variables.no_custom_fields_description', { level })}
          />
        }
        aria-label={t('editor.variables.assigned_custom_fields', 'Assigned custom fields')}
        columns={columns}
        rows={rows}
      />

      {confirmingField && (
        <DeleteCustomFieldConfirmDialog
          open
          level={level}
          onConfirm={() => {
            onDelete?.(confirmingField.id);
            setConfirmingField(null);
          }}
          onCancel={() => setConfirmingField(null)}
        />
      )}
    </>
  );
}

export function AssignedCustomFields({ level, query, onDelete }: AssignedCustomFieldsProps) {
  const { t } = useTranslation();
  const { reset } = useQueryErrorResetBoundary();
  const columns = useMemo(() => buildColumns(t), [t]);

  return (
    <ErrorBoundary
      onReset={reset}
      fallbackRender={({ resetErrorBoundary }) => (
        <ListView
          withHeader
          aria-label={t('editor.variables.assigned_custom_fields', 'Assigned custom fields')}
          ErrorState={
            <div role="alert">
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('editor.variables.error_loading_custom_fields', 'Unable to load custom fields')}
              </Typography>
              <Button onClick={resetErrorBoundary}>{t('editor.variables.try_again', 'Try again')}</Button>
            </div>
          }
          columns={columns}
          rows={[]}
        />
      )}
      onError={(error, info) => {
        trackError(new Error('AssignedCustomFields rendering failed', { cause: error }), {
          component: 'AssignedCustomFields',
          errorFrom: 'editor-remote',
          reactErrorInfo: info,
        });
      }}
    >
      <Suspense
        fallback={
          <ListView
            withHeader
            loading
            aria-label={t('editor.variables.assigned_custom_fields', 'Assigned custom fields')}
            columns={columns}
            rows={[]}
          />
        }
      >
        <AssignedCustomFieldsLoader level={level} query={query} onDelete={onDelete} />
      </Suspense>
    </ErrorBoundary>
  );
}
